import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Box from "../../../../../components/Box";
import { KTCard } from "../../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../../_metronic/helpers/components/KTCardBody";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../../../components/CustomSingleSelect";
import CustomNumberInput from "../../../../../components/CustomNumberInput";
import CustomTextInput from "../../../../../components/CustomTextInput";
import { storeAreYouSureModalCallback } from "../../../../../redux/reducers/authSlice";
import { brutten, fiyatYaz, HESAP_YONU, netten, sayiYaz, yuvarla } from "../../../../../helpers/yakitHesap";
import { YakitSecenekleri } from "../../../../../services/YakitService";

/** Fişin bir yakıt satırı (ekranda). */
export interface YakitSatirModeli {
    anahtar: number
    yakitTuru?: CustomSingleSelectModel
    birim?: CustomSingleSelectModel
    kdvOrani?: CustomSingleSelectModel
    miktar: number
    birimFiyat: number
    net: number
    kdv: number
    brut: number
    hesapYonu: number
    not?: string
    isSelected?: boolean
}

interface Props {
    satirlar: YakitSatirModeli[]
    setSatirlar: (satirlar: YakitSatirModeli[]) => void
    secenekler?: YakitSecenekleri
    /** TRY dışındaysa tutarların yanına yazılır ("EUR"). */
    dovizEki: string
    yeniAnahtar: () => number
}

const sayi = (s: string | undefined) => { const n = Number(s); return s == null || s === '' || isNaN(n) ? 0 : n; }
const metin = (n: number | undefined) => n == null || n == 0 ? '' : String(n);

/**
 * Yakıt detayları gridi (27.10.2026, #490): standart kayıt gridi (AmbarAdd › Sipariş Detayları gibi). Grid üstünde
 * Ekle / Güncelle / Sil; Ekle ya da Güncelle formu açar, Kaydet satırı gride yazar. Hesap: miktar / birim fiyat / KDV
 * değişince KDV hariç tutardan brüte, brüt değişince geriye (son değişen alan yönü belirler; sunucuyla aynı kural).
 */
const YakitSatirlari = ({ satirlar, setSatirlar, secenekler, dovizEki, yeniAnahtar }: Props) => {
    const dispatch = useDispatch();
    const kartRef = useRef<any>(null);

    const [acik, setAcik] = useState<boolean>(false);
    const [duzenlenen, setDuzenlenen] = useState<number | undefined>(undefined);
    const [yakitTuru, setYakitTuru] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [birim, setBirim] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [kdvOrani, setKdvOrani] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [miktar, setMiktar] = useState<string | undefined>(undefined);
    const [birimFiyat, setBirimFiyat] = useState<string | undefined>(undefined);
    const [brut, setBrut] = useState<string | undefined>(undefined);
    const [yon, setYon] = useState<number>(HESAP_YONU.NETTEN);
    const [not, setNot] = useState<string | undefined>(undefined);

    const oran = (k: CustomSingleSelectModel | undefined) => Number(k?.additionalValue1 ?? 0);

    // Formdaki anlık hesap
    const hesap = yon == HESAP_YONU.BRUTTEN
        ? brutten(sayi(miktar), sayi(brut), oran(kdvOrani))
        : netten(sayi(miktar), sayi(birimFiyat), oran(kdvOrani));

    const varsayilan = (liste: { value: string, label: string, ek?: string }[] | undefined, id?: string) => {
        const x = liste?.find(y => y.value == id);
        return x ? { label: x.label, value: x.value, additionalValue1: x.ek } : undefined;
    }

    const kaydir = () => {
        if (!kartRef.current)
            return;
        const ust = kartRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top: ust, behavior: "smooth" });
    }

    const formuTemizle = () => {
        setDuzenlenen(undefined);
        setYakitTuru(varsayilan(secenekler?.yakitTurleri, secenekler?.varsayilanYakitTuruId));
        setBirim(varsayilan(secenekler?.birimler, secenekler?.varsayilanBirimId));
        setKdvOrani(varsayilan(secenekler?.kdvOranlari, secenekler?.varsayilanKdvOraniId));
        setMiktar(undefined);
        setBirimFiyat(undefined);
        setBrut(undefined);
        setYon(HESAP_YONU.NETTEN);
        setNot(undefined);
    }

    const ekleIptal = () => {
        if (!acik)
            formuTemizle();
        setAcik(!acik);
        kaydir();
    }

    const guncelle = () => {
        const s = satirlar.filter(x => x.isSelected)[0];
        setDuzenlenen(s.anahtar);
        setYakitTuru(s.yakitTuru);
        setBirim(s.birim);
        setKdvOrani(s.kdvOrani);
        setMiktar(metin(s.miktar));
        setBirimFiyat(metin(s.birimFiyat));
        setBrut(metin(s.brut));
        setYon(s.hesapYonu);
        setNot(s.not);
        setAcik(true);
        kaydir();
    }

    const sil = () => setSatirlar(satirlar.filter(x => !x.isSelected));

    const satiriKaydet = () => {
        if (!yakitTuru || !birim || !kdvOrani) {
            toast.error('Yakıt türü, birim ve KDV oranını seçin.');
            return;
        }
        if (sayi(miktar) <= 0) {
            toast.error('Miktar sıfırdan büyük olmalı.');
            return;
        }
        if (hesap.birimFiyat <= 0 || hesap.brut <= 0) {
            toast.error('Birim fiyat ya da brüt toplam girin; sıfırdan büyük olmalı.');
            return;
        }

        const satir: YakitSatirModeli = {
            anahtar: duzenlenen ?? yeniAnahtar(),
            yakitTuru, birim, kdvOrani,
            miktar: yuvarla(sayi(miktar)),
            birimFiyat: hesap.birimFiyat,
            net: hesap.net,
            kdv: hesap.kdv,
            brut: hesap.brut,
            hesapYonu: yon,
            not: not
        };

        setSatirlar(duzenlenen != null ? satirlar.map(x => x.anahtar == duzenlenen ? satir : { ...x, isSelected: false }) : [...satirlar.map(x => ({ ...x, isSelected: false })), satir]);
        formuTemizle();
        setAcik(false);
    }

    const secimDegistir = (anahtar: number) => setSatirlar(satirlar.map(x => x.anahtar == anahtar ? { ...x, isSelected: !x.isSelected } : x));

    const hepsiniSec = () => {
        const hepsi = satirlar.length > 0 && satirlar.every(x => x.isSelected);
        setSatirlar(satirlar.map(x => ({ ...x, isSelected: !hepsi })));
    }

    const liste = (al: (s: YakitSecenekleri) => { value: string, label: string, ek?: string }[]) =>
        () => Promise.resolve({ isSuccess: true, data: secenekler ? al(secenekler).map(x => ({ label: x.label, value: x.value, ek: x.ek })) : [] });

    const secili = satirlar.filter(x => x.isSelected).length;
    const birimler = Array.from(new Set(satirlar.map(s => s.birim?.value)));
    const toplamMiktar = birimler.length == 1 ? yuvarla(satirlar.reduce((t, s) => t + s.miktar, 0)) : undefined;
    const toplam = (al: (s: YakitSatirModeli) => number) => sayiYaz(yuvarla(satirlar.reduce((t, s) => t + al(s), 0)));

    return (
        <div className='containerPart' ref={kartRef}>
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <h4 className="mb-10 text-black">Yakıt Detayları</h4>

                    {
                        acik && (
                            <React.Fragment>
                                <div className="row">
                                    <div className="col-md-3">
                                        <CustomSingleSelect labelText="Yakıt Türü" isLabelRequired selectedValue={yakitTuru} onChange={setYakitTuru} onMenuOpen={liste(x => x.yakitTurleri)} labelName='label' valueName='value' />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomNumberInput labelText="Miktar" isLabelRequired value={miktar} setValue={(v: string) => { setMiktar(v); setYon(HESAP_YONU.NETTEN); }} />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomSingleSelect labelText="Birim" isLabelRequired selectedValue={birim} onChange={setBirim} onMenuOpen={liste(x => x.birimler)} labelName='label' valueName='value' />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomNumberInput labelText="Birim Fiyat" isLabelRequired value={yon == HESAP_YONU.BRUTTEN ? metin(hesap.birimFiyat) : birimFiyat} setValue={(v: string) => { setBirimFiyat(v); setYon(HESAP_YONU.NETTEN); }} />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-3">
                                        <CustomSingleSelect labelText="KDV %" isLabelRequired selectedValue={kdvOrani} onChange={setKdvOrani} onMenuOpen={liste(x => x.kdvOranlari)} labelName='label' valueName='value' additionalValue1Name='ek' />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomTextInput labelText="Toplam Tutar (KDV hariç)" isDisabled value={sayiYaz(hesap.net)} />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomTextInput labelText="KDV Tutar" isDisabled value={sayiYaz(hesap.kdv)} />
                                    </div>
                                    <div className="col-md-3">
                                        <CustomNumberInput labelText="Brüt Toplam" isLabelRequired value={yon == HESAP_YONU.BRUTTEN ? brut : metin(hesap.brut)} setValue={(v: string) => { setBrut(v); setYon(HESAP_YONU.BRUTTEN); }} />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-6">
                                        <CustomTextInput labelText="Not" value={not} setValue={setNot} />
                                    </div>
                                    <div className="col-md-6 d-flex align-items-end">
                                        <div className="text-muted fs-7">Brüt toplamı değiştirirseniz KDV hariç tutar ve birim fiyat geriye hesaplanır. Birim fiyat en çok 4 ondalık.</div>
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <Box alignItems="flex-end">
                                            <Box onClick={satiriKaydet} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                <Box mr="5px" justifyContent="center">
                                                    <i className="text-white fa fa-check" />
                                                </Box>
                                                <Box justifyContent="center">
                                                    <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </div>
                                </div>

                                <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                            </React.Fragment>
                        )
                    }

                    <Box className={acik ? "mt-5" : ""} flexDirection="row">
                        <Box onClick={ekleIptal} className={"actionIcon p-2 " + (!acik ? "bg-success" : "")} opacity={"1"} bg={acik ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className={"text-white fa " + (!acik ? 'fa-plus' : 'fa-times')} />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">{!acik ? 'Ekle' : 'İptal Et'}</span>
                            </Box>
                        </Box>

                        <Box onClick={secili == 1 ? guncelle : undefined} opacity={secili == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-edit" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                            </Box>
                        </Box>

                        <Box onClick={secili > 0 ? () => dispatch(storeAreYouSureModalCallback(sil)) : undefined} opacity={secili > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-trash" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                            </Box>
                        </Box>
                    </Box>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <div className='table-responsive'>
                                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th style={{ width: 1 }}>
                                                <div className='d-flex'>
                                                    <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                        <input onChange={hepsiniSec} className='form-check-input' type='checkbox' checked={satirlar.length > 0 && satirlar.every(x => x.isSelected)} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th>Yakıt Türü</th>
                                            <th className="text-end">Miktar</th>
                                            <th>Birim</th>
                                            <th className="text-end">Birim Fiyat</th>
                                            <th className="text-end">Toplam Tutar</th>
                                            <th className="text-end">KDV %</th>
                                            <th className="text-end">KDV Tutar</th>
                                            <th className="text-end">Brüt Toplam</th>
                                            <th>Not</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            satirlar.length == 0 && (
                                                <tr>
                                                    <td className="text-center" colSpan={99}>Kayıt bulunamadı. "Ekle" ile yakıt satırı girin.</td>
                                                </tr>
                                            )
                                        }
                                        {
                                            satirlar.map(s =>
                                                <tr key={s.anahtar}>
                                                    <td style={{ width: 1 }}>
                                                        <div className='d-flex'>
                                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input onChange={() => secimDegistir(s.anahtar)} className='form-check-input' type='checkbox' checked={!!s.isSelected} />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td>{s.yakitTuru?.label}</td>
                                                    <td className="text-end">{sayiYaz(s.miktar)}</td>
                                                    <td>{s.birim?.label}</td>
                                                    <td className="text-end">{fiyatYaz(s.birimFiyat)}</td>
                                                    <td className="text-end">{sayiYaz(s.net)}{dovizEki}</td>
                                                    <td className="text-end">{s.kdvOrani?.label}</td>
                                                    <td className="text-end">{sayiYaz(s.kdv)}{dovizEki}</td>
                                                    <td className="text-end">{sayiYaz(s.brut)}{dovizEki}</td>
                                                    <td>{s.not}</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    {
                                        satirlar.length > 0 && (
                                            <tfoot>
                                                <tr className="ebbe-dip-toplam">
                                                    <td style={{ width: 1 }}></td>
                                                    <td>Toplam</td>
                                                    <td className="text-end">{toplamMiktar != null ? sayiYaz(toplamMiktar) : '—'}</td>
                                                    <td>{toplamMiktar != null ? satirlar[0]?.birim?.label : ''}</td>
                                                    <td></td>
                                                    <td className="text-end">{toplam(s => s.net)}{dovizEki}</td>
                                                    <td></td>
                                                    <td className="text-end">{toplam(s => s.kdv)}{dovizEki}</td>
                                                    <td className="text-end">{toplam(s => s.brut)}{dovizEki}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        )
                                    }
                                </table>
                            </div>
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default YakitSatirlari;
